import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  CircularProgress
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { authService } from '../services/authService';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Token de recuperação não encontrado. Solicite um novo link de recuperação.');
    }
  }, [token]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError('Token de recuperação inválido ou ausente.');
      return;
    }

    if (formData.newPassword.length < 6) {
      setError('A nova senha deve ter no mínimo 6 caracteres.');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('As senhas não coincidem.');
      return;
    }

    setLoading(true);

    try {
      const response = await authService.resetPassword({
        token,
        newPassword: formData.newPassword,
      });

      if (response.data?.success) {
        setSuccess(true);
        toast.success('Senha redefinida com sucesso!');
      } else {
        setError(response.data?.error || 'Erro ao redefinir senha.');
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'Erro ao redefinir senha. O link pode ter expirado.';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Card
        sx={{
          background: 'rgba(10, 10, 10, 0.85)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 107, 53, 0.3)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h4"
              sx={{
                fontFamily: 'Orbitron',
                fontWeight: 900,
                background: 'linear-gradient(45deg, #FF6B35, #00D4FF)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2,
              }}
            >
              Nova Senha
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.7 }}>
              Defina sua nova senha de acesso à conta de DJ
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {success ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Alert severity="success" sx={{ mb: 3 }}>
                Senha alterada com sucesso! Você já pode entrar com sua nova senha.
              </Alert>
              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={() => navigate('/login')}
                sx={{
                  py: 1.5,
                  bgcolor: '#FF6B35',
                  '&:hover': { bgcolor: '#E0531D' },
                }}
              >
                Ir para o Login
              </Button>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Nova Senha"
                name="newPassword"
                type="password"
                value={formData.newPassword}
                onChange={handleChange}
                required
                disabled={!token || loading}
                sx={{ mb: 3 }}
                helperText="Mínimo de 6 caracteres"
              />

              <TextField
                fullWidth
                label="Confirmar Nova Senha"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                disabled={!token || loading}
                sx={{ mb: 3 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading || !token || !formData.newPassword || !formData.confirmPassword}
                sx={{
                  py: 1.5,
                  mb: 3,
                  bgcolor: '#FF6B35',
                  '&:hover': {
                    bgcolor: '#E0531D',
                  },
                  '&:disabled': {
                    bgcolor: 'rgba(255, 107, 53, 0.3)',
                  },
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Salvar Nova Senha'}
              </Button>

              <Box sx={{ textAlign: 'center' }}>
                <Link
                  component="button"
                  type="button"
                  onClick={() => navigate('/login')}
                  sx={{
                    color: '#FF6B35',
                    textDecoration: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      color: '#FF8C5A',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Voltar para o Login
                </Link>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default ResetPassword;
